/**
 * Job-aid articles, transcribed from the authored `<Title>.html` job aids.
 * Screenshots are the same captures, extracted from those files and served
 * statically from `apps/web/public/assets/help/<id>/`; annotation overlays
 * keep their original percentage coordinates.
 *
 * NOTE: the source HTML lives in a `jobaids/` folder that is deliberately
 * gitignored (.gitignore) — it is internal authoring material, not part of
 * this repo, so a fresh clone will not have it. Get a copy before attempting
 * to re-transcribe.
 *
 * GENERATED — do not hand-edit. Re-transcribe from the source HTML instead,
 * so the two cannot drift.
 *
 * All names appearing in screenshots are fictional (jobaids screenshot
 * standards).
 */

import type { HelpArticle } from './helpContent';

export const jobAidArticles: HelpArticle[] = [

  {
    id: 'sign-in-first-time',
    collectionId: 'getting-started',
    title: 'Sign in for the first time',
    summary:
      "Activate a new team member's login, then set your own password on first sign-in — no password is ever shared.",
    audience: 'Everyone, plus Org Admins',
    duration: '~4 minutes',
    version: '3.0',
    updatedAt: '2026-09-04',
    blocks: [
      {
        kind: 'callout',
        label: 'Who this is for',
        text: "Two halves. **Part 1** is for the **Org Admin** switching on a new team member's login. **Part 2** is for that **team member** signing in for the first time. Nobody needs to send anybody a password.",
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Being on the team is not the same as being able to sign in',
        text: 'A new member can read **Active** and still not be able to log in. Their record carries two separate states — **Active** (they are a member) and **Activated** (their login works). A member created without login access shows **Active · Not Activated**.',
      },
      {
        kind: 'heading',
        text: 'Part 1 — Activate the login [[Org Admin]]',
      },
      {
        kind: 'step',
        num: '1',
        action: 'Open [[Team]], click the person, and find [[Login Access]] on their record.',
        result: 'either **Login not activated** with an **Activate Login** button, or confirmation that their login already works.',
      },
      {
        kind: 'image',
        src: '/assets/help/sign-in-first-time/01.png',
        alt: "A team member's record showing their role, location and supervisor, with a Login Access section reading login not activated beside an Activate Login button, and a separate Status section showing them as Active.",
        caption: '**Active, but not activated.** A real member who cannot yet sign in — the correct state for someone who has not started.',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Click [[Activate Login]]. A **Temporary Password Generated** box appears — click [[Done]] to close it.',
        result: "the member's record now reads **Activated**.",
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Do not copy or share that password',
        text: 'You do not need it, and neither does the team member. Close the box.\n\nPassing a password to someone by email or chat means a credential that opens clinical records is sitting in an inbox you do not control. The team member sets their own in Part 2, and you will never see it — which is how it should be.',
      },
      {
        kind: 'image',
        src: '/assets/help/sign-in-first-time/02.png',
        alt: 'A Temporary Password Generated dialog showing a masked password with a Copy button and a Done button.',
        caption: '**Close this.** The temporary password is generated automatically. Ignore it and click **Done** — it is never used.',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Tell the team member their account is ready, and that they should use [[Forgot password?]] the first time they sign in.',
        result: 'nothing else to do. Part 2 is theirs.',
      },
      {
        kind: 'heading',
        text: 'Part 2 — Sign in for the first time [[New team member]]',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Go to **app.upskillaba.com** and click [[Sign In]].',
        result: 'the UpskillABA sign-in page.',
      },
      {
        kind: 'image',
        src: '/assets/help/sign-in-first-time/03.png',
        alt: 'The UpskillABA sign-in landing page with a Sign In button.',
        caption: '**Start here.** The same page every time — bookmark it.',
      },
      {
        kind: 'step',
        num: '5',
        action: 'Enter the **work email address your account was created with**, then [[Next]].',
        result: 'a password box.',
      },
      {
        kind: 'image',
        src: '/assets/help/sign-in-first-time/04.png',
        alt: 'The sign-in screen asking for an email address, with a Next button.',
        caption: '**Use your work address.** A personal address will not be recognised, even if you use it everywhere else.',
      },
      {
        kind: 'step',
        num: '6',
        action: 'Do not type anything. Click [[Forgot password?]]',
        result: 'a **Verify your identity** panel.',
      },
      {
        kind: 'image',
        src: '/assets/help/sign-in-first-time/05.png',
        alt: 'The password entry screen showing a password field, a Forgot password link and a Sign in button.',
        caption: '**This is the step people miss.** You have no password yet, so skip the box entirely and choose **Forgot password?**',
      },
      {
        kind: 'step',
        num: '7',
        action: 'Choose [[Email code to …]] — your own address is already filled in.',
        result: 'a code sent to your inbox, and a box to type it into.',
      },
      {
        kind: 'image',
        src: '/assets/help/sign-in-first-time/06.png',
        alt: "A Verify your identity panel offering to email a verification code to the user's own address.",
        caption: '**Proving it is you.** The code arrives in seconds and lasts 30 minutes.',
      },
      {
        kind: 'step',
        num: '8',
        action: 'Enter the code from the email, then set your own password.',
        result: 'confirmation that your password has been updated.',
      },
      {
        kind: 'image',
        src: '/assets/help/sign-in-first-time/07.png',
        alt: 'A Reset password screen with New password and Re-enter password fields, and the complexity rules stated above them.',
        caption: '**Your password, known only to you.** At least 8 characters, and three of: uppercase, lowercase, number, symbol.',
      },
      {
        kind: 'image',
        src: '/assets/help/sign-in-first-time/08.png',
        alt: "A confirmation screen reading You've updated your password with a Next button.",
        caption: '**Done.** Click **Next** to continue into the platform.',
      },
      {
        kind: 'step',
        num: '9',
        action: 'Continue into UpskillABA.',
        result: 'your dashboard.',
      },
      {
        kind: 'image',
        src: '/assets/help/sign-in-first-time/09.png',
        alt: 'The UpskillABA dashboard on first sign-in, showing the left navigation and a welcome panel.',
        caption: "**You are in.** What you see depends on your role — a Behavior Technician's menu is shorter than an Org Admin's. That is expected, not a fault.",
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What it means'],
        rows: [
          ['**"We couldn\'t find an account with this email address or password"**', 'Either the address is not the one your account was created with, or your login has not been activated yet. Check with your Org Admin rather than retrying — repeated failed attempts can lock the account.'],
          ['No **Forgot password?** link on the password screen', 'You are on the wrong screen. Go back and re-enter your email address; the link appears on the step that asks for a password.'],
          ['The code never arrives', 'Check junk mail. Codes expire after 30 minutes — if yours has, start again from **Forgot password?** to get a fresh one.'],
          ['You signed in, but sections are missing from the menu', 'Expected. The menu reflects your role. If you believe you need access to something you cannot see, that is a question for your Org Admin.'],
        ],
      },
      {
        kind: 'callout',
        label: 'Nobody should ever ask you for your password',
        text: 'Not your Org Admin, not UpskillABA support. If anyone does, they are not who they say they are. You set it in step 8 and you are the only person who knows it.',
      },
    ],
  },
  {
    id: 'find-your-way-around',
    collectionId: 'getting-started',
    title: 'Find your way around the platform',
    summary: "What each area of UpskillABA is for, and why your menu looks different from a colleague's.",
    audience: 'Everyone',
    duration: '~5 minutes',
    version: '2.0',
    updatedAt: '2026-08-26',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        text: 'You need a working sign-in. If you have not logged in before, start with **Sign in for the first time** and come back here.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: "Your menu is not everyone's menu",
        text: 'The sidebar shows only what your role can reach. If a colleague describes a page you cannot find, you are probably not missing it — you do not have access to it. That is by design, and the table below shows who sees what.',
      },
      {
        kind: 'heading',
        text: 'The five groups in the sidebar',
      },
      {
        kind: 'step',
        num: '1',
        action: 'Look down the left-hand sidebar. Items are gathered under headings.',
        result: 'up to five groups: **MAIN**, **WORKFLOWS**, **INSIGHTS**, **ORGANIZATION** and **SETTINGS**.',
      },
      {
        kind: 'table',
        headers: ['Group', 'Holds', 'Use it to'],
        rows: [
          ['**MAIN**', 'Dashboard', 'Land here at sign-in; jump to your workspace.'],
          ['**WORKFLOWS**', 'Preference Assessment · Red Dot Assessment', '**Do** clinical work — fill in and submit assessments.'],
          ['**INSIGHTS**', 'Red Dot', '**Read** results across clients — the analytics report.'],
          ['**ORGANIZATION**', 'Clients · Team · Centers · Goals · Roles', 'Set up and maintain who and what the org contains.'],
          ['**SETTINGS**', 'Settings', 'Your profile and organization configuration.'],
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'The two Red Dot entries are different things',
        text: '[[WORKFLOWS › Red Dot Assessment]] is where you **fill in and submit** an assessment, and where your own submissions live. [[INSIGHTS › Red Dot]] is the **report** — counts, trends and tables across every client you can see. Technicians only get the first.',
      },
      {
        kind: 'heading',
        text: 'What your role can reach',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Find your role in the table below.',
        result: 'exactly which sidebar items you should have.',
      },
      {
        kind: 'table',
        headers: ['Sidebar item', 'RBT', 'BCBA', 'Clinical Director', 'Org Admin'],
        rows: [
          ['Dashboard', '✓', '✓', '✓', '✓'],
          ['Preference Assessment', '—', '✓', '✓', '✓'],
          ['Red Dot Assessment', '✓', '✓', '✓', '✓'],
          ['Red Dot (report)', '—', '✓', '✓', '✓'],
          ['Clients', '✓', '✓', '✓', '✓'],
          ['Team', '—', '✓', '✓', '✓'],
          ['Centers', '—', '—', '—', '✓'],
          ['Goals', '—', '✓', '✓', '✓'],
          ['Roles', '—', '—', '—', '✓'],
          ['Settings', '✓', '✓', '✓', '✓'],
        ],
      },
      {
        kind: 'text',
        text: 'A technician sees four items; a BCBA or Clinical Director eight; an Org Admin all ten. **Centers** and **Roles** are the two administrative pages nobody clinical can reach.',
      },
      {
        kind: 'image',
        src: '/assets/help/find-your-way-around/01.png',
        alt: 'The dashboard as seen by a behavior technician, with a short sidebar containing only Dashboard, Red Dot Assessment, Clients and Settings.',
        caption: "**A technician's view.** Four items — everything needed to work a caseload and nothing else.",
      },
      {
        kind: 'image',
        src: '/assets/help/find-your-way-around/02.png',
        alt: 'The same dashboard as seen by an Org Admin, with a longer sidebar including the Insights group and the Centers, Goals and Roles administrative pages.',
        caption: "**An Org Admin's view.** The same dashboard, a much longer sidebar.",
      },
      {
        kind: 'heading',
        text: 'Same page, different contents',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Open [[Clients]].',
        result: 'only the clients you are entitled to — not the whole organization.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Scoping is the other half of permissions',
        items: [
          '**RBT** — only clients on their own caseload.',
          '**BCBA** — the clients they oversee.',
          '**Clinical Director / Org Admin** — every client in the organization.',
        ],
      },
      {
        kind: 'heading',
        text: 'Where the day-to-day work happens',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Open the area that matches your job.',
      },
      {
        kind: 'table',
        headers: ['If you are a…', 'Start at', 'Then read'],
        rows: [
          ['Behavior technician', '**Red Dot Assessment**', '**Raise a Red Dot**'],
          ['BCBA', '**Dashboard**, then **Red Dot**', '**Work and close a Red Dot** · **Read the Red Dot report**'],
          ['Clinical Director', '**Red Dot** report', '**Read the Red Dot report**'],
          ['Org Admin', '**Team** and **Clients**', '**Add a team member** · **Create a client record**'],
        ],
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ["A colleague's menu item is missing from yours", 'Your role does not include it. Expected — check the table above.'],
          ['Your sidebar has fewer items than the table says', 'Your role may not be set. Ask an Org Admin to check your record.'],
          ['**Clients** is empty', 'No clients are assigned to you yet. Ask an Org Admin.'],
          ['A "Session Expiring" box appears', 'Normal — the platform signs you out when idle, for HIPAA. Click **Continue Session**.'],
          ['You landed somewhere unexpected', 'Pages you cannot reach redirect to the Dashboard rather than showing an error.'],
        ],
      },
    ],
  },
  {
    id: 'create-a-client-record',
    collectionId: 'clients',
    title: 'Create a client record',
    summary: 'Add a new client, assign their center and overseeing BCBA, and get them ready for clinical work.',
    audience: 'Org Admins',
    duration: '~3 minutes',
    version: '2.0',
    updatedAt: '2026-08-26',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          'You must be signed in as an **Org Admin**.',
          "The client's **center** must already exist — see **Create and manage centers**.",
          'Their **overseeing BCBA** should already be on the team — see **Add a team member**.',
          "Have the child's **legal name and date of birth** to hand.",
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'This is real clinical data',
        text: 'A client record is PHI from the moment you save it. Enter the name exactly as it appears on the referral, and do not use a placeholder you intend to correct later — the record is referenced by assessments, goals and reports as soon as it exists.',
      },
      {
        kind: 'heading',
        text: 'Add the client',
      },
      {
        kind: 'step',
        num: '1',
        action: 'Open [[Clients]] from the sidebar and click [[Add Client]].',
        result: 'the **Add Client** box. Only **First Name** and **Last Name** are required — everything else can be filled in later.',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Enter [[First Name]], [[Last Name]] and [[Date of Birth]], and pick a [[Gender]].',
        result: 'the age calculated from the date of birth on the finished record.',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Choose the [[Primary Center]] and the [[Assigned BCBA]].',
        result: "both reflected in the client's header once saved.",
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'The Assigned BCBA list is not filtered by role',
        text: 'The dropdown offers **everyone** on the team — RBTs and the Org Admin included, not just BCBAs. Nothing stops you picking the wrong kind of person, so check the name against your team list. This field sets clinical responsibility: Red Dots raised for this client route to whoever is named here.',
      },
      {
        kind: 'image',
        src: '/assets/help/create-a-client-record/01.png',
        alt: 'The Add Client box completed with first name, last name, date of birth, gender, and a primary center and assigned BCBA chosen from dropdowns, with optional phone, email, external ID and notes fields left blank.',
        caption: '**Add Client, completed.** Phone, email, **External ID / MRN** and notes are optional — fill the MRN if you reconcile against another system.',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Click [[Add Client]] at the bottom of the box.',
        result: 'the box close and the new client at the top of the list, marked **Active**, with their center and BCBA shown.',
      },
      {
        kind: 'image',
        src: '/assets/help/create-a-client-record/02.png',
        alt: 'The Clients list showing the newly created client at the top with their date of birth, an Active status, their centre, their assigned BCBA and a Replace action.',
        caption: '**Created.** The client is **Active** immediately — there is no draft state.',
        annotations: [
          { shape: 'badge', tone: 'ok', label: '✓', left: '6%', top: '9%' },
        ],
      },
      {
        kind: 'heading',
        text: 'What the client still needs',
      },
      {
        kind: 'text',
        text: 'A new client is not yet ready for clinical work. Three things are missing:',
      },
      {
        kind: 'table',
        headers: ['Missing', 'Why it matters', 'See'],
        rows: [
          ['**An assigned RBT**', 'Technicians cannot see the client at all until they are on their caseload, so nobody can raise a Red Dot.', '**Assign a client to a team member**'],
          ['**Goals**', 'Red Dots are raised against a goal. With none, the goal dropdown is empty.', '**Add and edit goals**'],
          ['**A confirmed overseeing BCBA**', 'Sets who is clinically accountable and who receives Red Dots.', '**Set the overseeing BCBA**'],
        ],
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['No **Add Client** button', 'You are not signed in as an Org Admin.'],
          ['The center you need is missing', 'It has not been created. See **Create and manage centers**.'],
          ['The BCBA you need is missing', 'They are not on the team yet. See **Add a team member**.'],
          ['Wrong date of birth', 'Open the record and use **Edit** under Demographics.'],
          ['You created a duplicate', 'Do not create a second. Correct the original — clients cannot be hard-deleted, only discharged.'],
          ['An RBT still cannot see them', 'Expected. Assigning a BCBA is not the same as assigning a caseload — see **Assign a client to a team member**.'],
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Adding many clients at once',
        text: '[[Bulk Upload]] beside **Add Client** takes a file. Use it for an initial migration; use this form for a single referral.',
      },
    ],
  },
  {
    id: 'assign-client-to-clinician',
    collectionId: 'clients',
    title: 'Assign a client to a team member',
    summary: "Put a client on a clinician's caseload so that team member can see and work with them.",
    audience: 'Org Admins',
    duration: '~2 minutes',
    version: '2.0',
    updatedAt: '2026-08-26',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          'You must be signed in as an **Org Admin**.',
          'The **team member** must already exist — see **Add a team member**.',
          'The **client** must already exist — see **Create a client record**.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Why this matters',
        text: 'Until a client is on their caseload, a clinician cannot see that client **at all** — not in their client list, and not in the client picker when they start an assessment. If a team member reports a client is "missing", this is almost always the reason.',
      },
      {
        kind: 'heading',
        text: 'Assign the client',
      },
      {
        kind: 'step',
        num: '1',
        action: "In [[Clients]], click the client's name to open their record.",
        result: "the client's panel, with **Edit Client**, **Assign Providers** and **Replace BCBA** across the top, and a **Clinicians** count in the header.",
      },
      {
        kind: 'image',
        src: '/assets/help/assign-client-to-clinician/01.png',
        alt: 'A client record showing the center, assigned BCBA and a clinician count of zero in the header, with Edit Client, Assign Providers and Replace BCBA buttons, and an Assigned Clinicians section reading no clinicians assigned to this client yet.',
        caption: '**Before assigning.****Clinicians** reads **0** and the **Assigned Clinicians** table is empty.',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Click [[Assign Clinician]] in the [[Assigned Clinicians]] section.',
        result: 'the **Assign providers** picker, listing everyone in the organization with their center and role beneath each name.',
      },
      {
        kind: 'callout',
        label: 'Two buttons, one picker',
        text: '[[Assign Providers]] at the top of the record and [[Assign Clinician]] in the Clinicians section open the **same** picker. Use whichever is closer to hand.',
      },
      {
        kind: 'step',
        num: '3',
        action: "Click the person's row to select them. Use the search box above the list if the roster is long.",
        result: 'a **SELECTED (1)** section appear, and the button at the bottom change from **Assign** to **Assign 1**.',
      },
      {
        kind: 'image',
        src: '/assets/help/assign-client-to-clinician/02.png',
        alt: "The Assign providers picker listing every provider in the organization, each row showing the person's name with their center and role underneath, and Cancel and Assign buttons at the bottom.",
        caption: '**Assign providers.** Each row shows **center · role**, so you can tell two similar names apart before selecting.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Watch the button label',
        text: 'The button only reads **Assign 1** once someone is actually selected. If it still reads **Assign**, your click did not register — click the row again before continuing, or you will close the picker having changed nothing.',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Click [[Assign 1]].',
        result: "the picker close, **Clinicians** in the header change to **1**, and the person listed under **Assigned Clinicians** with their email and today's date.",
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Assigning more than one',
        text: 'Select several people before clicking the button — it counts up (**Assign 2**, **Assign 3**) so you can check the number before committing.',
      },
      {
        kind: 'heading',
        text: 'Confirm you are finished',
      },
      {
        kind: 'image',
        src: '/assets/help/assign-client-to-clinician/03.png',
        alt: 'The client record after assignment, showing a clinician count of one in the header and the assigned clinician listed with their email address and assignment date.',
        caption: '**After assigning.****Clinicians** increases and the person appears under **Assigned Clinicians** with the date.',
        annotations: [
          { shape: 'badge', tone: 'ok', label: '✓', left: '6%', top: '9%' },
        ],
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['The person is not in the picker', 'They were not created, or hold no role. Check **Team** — see **Add a team member**.'],
          ['The button still reads **Assign**', "Nobody is selected. Click the person's row and wait for **SELECTED (1)**."],
          ['**Clinicians** still shows 0', 'The assignment did not save. Re-open the picker and confirm **SELECTED (1)** before clicking **Assign 1**.'],
          ['The clinician still cannot see the client', 'Ask them to sign out and back in. Caseload is read at sign-in.'],
          ['Assigned the wrong person', 'Use the remove control on their row in **Assigned Clinicians**, then assign the right person.'],
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: "What's next",
        text: 'Assigning a clinician does not set clinical responsibility. To name the BCBA accountable for this client, see **Set the overseeing BCBA**.',
      },
    ],
  },
  {
    id: 'set-the-overseeing-bcba',
    collectionId: 'clients',
    title: 'Set the overseeing BCBA',
    summary: 'Name the BCBA clinically responsible for a client, or replace the current one.',
    audience: 'Org Admins',
    duration: '~2 minutes',
    version: '2.0',
    updatedAt: '2026-08-26',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          'You must be signed in as an **Org Admin**.',
          'The **client** must already exist — see **Create a client record**.',
          'The **BCBA** must already be on the team — see **Add a team member**.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'What the Overseeing BCBA actually controls',
        text: 'This is clinical accountability, not caseload access. The person named here is who **Red Dots and Calls to Action route to** when a technician raises one for this client, and whose name appears against the client on the Red Dot report. Assigning a clinician to the caseload is a separate job — see **Assign a client to a team member**.',
      },
      {
        kind: 'heading',
        text: 'Set or replace the Overseeing BCBA',
      },
      {
        kind: 'step',
        num: '1',
        action: 'Open the client from [[Clients]], then click [[Replace BCBA]].',
        result: "the **Replace Overseeing BCBA** box, showing the client's name and who currently holds the role.",
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'The list offers everyone, not just BCBAs',
        text: 'The dropdown includes RBTs, the Clinical Director and the Org Admin. Nothing prevents you naming a technician as the overseeing BCBA — and if you do, clinical escalations will route to someone who is not qualified to act on them. Check the name against your team list.',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Pick the new BCBA from [[Overseeing BCBA (single active holder)]].',
        result: 'the line **Replaces current holder:** beneath the dropdown update to name the person being replaced.',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Set [[Effective from]] if the change should start on a particular date, then click [[Save]].',
        result: "the new name in **ASSIGNED BCBA** in the client's header, and in the **Assigned BCBA** column of the Clients list.",
      },
      {
        kind: 'image',
        src: '/assets/help/set-the-overseeing-bcba/01.png',
        alt: 'The Replace Overseeing BCBA box showing the client name, a dropdown with the new overseeing BCBA selected, a line naming the current holder being replaced, an optional effective-from date and a Save button.',
        caption: '**Replace Overseeing BCBA.****Effective from** is optional and defaults to today. There is exactly one active holder — saving replaces the previous one, so you never remove the old holder first.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Known display quirk — check the header, not the Assignment panel',
        text: 'After saving, the client header shows the new BCBA correctly, but the **ASSIGNED BCBA** field further down under **Assignment** can still read **Not assigned**. This affects every client and does not mean the save failed. Trust the **header** and the **Clients list** column; both are correct.',
      },
      {
        kind: 'callout',
        label: 'If the client has open Red Dots',
        text: "Replacing the Overseeing BCBA on a client with open Red Dots may prompt you to decide what happens to them — move them to the new BCBA, keep them with the outgoing one, or leave them unassigned for an Org Admin to route. Read that prompt before confirming; unassigned items are nobody's responsibility and go overdue quietly.",
      },
      {
        kind: 'heading',
        text: 'Confirm you are finished',
      },
      {
        kind: 'image',
        src: '/assets/help/set-the-overseeing-bcba/02.png',
        alt: 'The client record after the change, showing the new overseeing BCBA in the header beside the centre and clinician count.',
        caption: '**Finished.****ASSIGNED BCBA** in the client header shows the new overseeing BCBA.',
        annotations: [
          { shape: 'badge', tone: 'ok', label: '✓', left: '6%', top: '9%' },
        ],
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['No **Replace BCBA** button', 'You are not signed in as an Org Admin.'],
          ['The BCBA you want is not listed', 'They are not on the team yet — see **Add a team member**.'],
          ['**Assignment** still says **Not assigned**', 'Known display quirk. Check the header and the Clients list instead.'],
          ['Header and list genuinely disagree', 'Refresh. If they still differ, report it — the record may not have saved.'],
          ['Red Dots still route to the old BCBA', 'Existing items keep their original assignee. Only new submissions follow the new holder.'],
          ['Assigned the wrong person', 'Use **Replace BCBA** again — it always replaces the single active holder.'],
        ],
      },
    ],
  },
  {
    id: 'transfer-a-client',
    collectionId: 'clients',
    title: 'Transfer a client to another BCBA',
    summary: "Move some or all of a clinician's caseload to a colleague, and decide what happens to their open Red Dots.",
    audience: 'Org Admins',
    duration: '~4 minutes',
    version: '1.0',
    updatedAt: '2026-08-27',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          'You must be signed in as an **Org Admin**.',
          'Know **which clients** are moving — you can transfer some or all of a caseload.',
          'Know **what should happen to open Red Dots**. You will be asked, and it is the one decision here you cannot undo by simply transferring back.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Which tool do I need?',
        text: 'Transfer Caseload moves several clients at once **and** handles their open Red Dots. Replace BCBA does neither.',
      },
      {
        kind: 'heading',
        text: 'Set up the transfer',
      },
      {
        kind: 'step',
        num: '1',
        action: 'Open [[Clients]] and click [[Transfer Caseload]] at the top of the list.',
        result: 'the **Configure Transfer** step, asking for a **From clinician** and a **To clinician**.',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Choose the [[From clinician]], then the [[To clinician]].',
        result: "a list of that clinician's clients appear beneath, under **Select clients to transfer**.",
      },
      {
        kind: 'callout',
        label: 'This picker names roles — use them',
        text: 'Every option reads **Name — Role**, so you can tell a BCBA from an RBT before you choose. Once you pick a **From** clinician, they disappear from the **To** list, so you cannot transfer someone to themselves.',
      },
      {
        kind: 'image',
        src: '/assets/help/transfer-a-client/01.png',
        alt: "The Configure Transfer step of the Transfer Caseload wizard, with a from clinician and a to clinician chosen from dropdowns that show each person's name followed by their role.",
        caption: '**Configure Transfer.** The client list only appears once both clinicians are chosen.',
      },
      {
        kind: 'heading',
        text: 'Choose the clients',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Tick the clients that should move, or use [[Select all]]. Then click [[Next]].',
        result: 'a running count — **1 of 2 clients selected** — beneath the list.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'You do not have to move the whole caseload',
        text: 'For a departure, use **Select all**. For a workload rebalance or a single complex client, tick just those. Anyone you leave unticked stays exactly where they are.',
      },
      {
        kind: 'image',
        src: '/assets/help/transfer-a-client/02.png',
        alt: "The client selection step showing the outgoing clinician's clients each with a checkbox and an Active badge, a Select all option, and a counter reading one of two clients selected.",
        caption: '**Select clients to transfer.** Check the counter before continuing — it is the quickest way to catch a mis-click.',
      },
      {
        kind: 'heading',
        text: 'Confirm, then decide about open Red Dots',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Read the [[Confirm Transfer]] summary, then click [[Transfer]].',
        result: 'it restate the move in one line — how many clients, from whom, to whom.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'There is a step the progress bar does not show',
        text: 'If any of the selected clients have **open Red Dots**, a **third** step appears after you click Transfer. The wizard starts out looking like two steps, so this catches people out — do not walk away at the confirm screen assuming you are finished.',
      },
      {
        kind: 'step',
        num: '5',
        action: 'Choose what happens to the open Red Dots, then click [[Transfer]] again.',
        result: 'a per-client breakdown and three options.',
      },
      {
        kind: 'table',
        headers: ['Option', 'What happens', 'Use when'],
        rows: [
          ['**Transfer to new BCBA**', 'Open Red Dots move with the client.', 'The usual choice — the incoming BCBA picks up the work.'],
          ['**Keep with outgoing BCBA**', 'They stay with the departing clinician.', 'They are nearly resolved and that person is still working.'],
          ['**Unassign — route to Org Admins**', 'They become unassigned; Org Admins are notified and can re-route.', 'You do not yet know who should own them.'],
        ],
      },
      {
        kind: 'image',
        src: '/assets/help/transfer-a-client/03.png',
        alt: 'The open Red Dots step listing the affected client and their count of open Red Dots, with three options to transfer them to the new BCBA, keep them with the outgoing BCBA, or unassign them and route to Org Admins.',
        caption: '**Open Red Dots.** The counts are per client, so you can see exactly what each choice affects.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Do not choose "Keep with outgoing BCBA" for a leaver',
        text: 'If the clinician is departing, their open Red Dots become invisible work — assigned to someone who will stop logging in, quietly going overdue on the Red Dot report. Transfer them, or unassign them so an Org Admin can re-route.',
      },
      {
        kind: 'heading',
        text: 'Confirm you are finished',
      },
      {
        kind: 'image',
        src: '/assets/help/transfer-a-client/04.png',
        alt: 'The Clients list after the transfer, showing the moved client now against the incoming BCBA while the client left unticked still shows the original BCBA.',
        caption: '**Transferred.** Only the ticked client moved — the other still shows the original BCBA.',
        annotations: [
          { shape: 'badge', tone: 'ok', label: '✓', left: '6%', top: '9%' },
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'If this is a departure, this is step one of two',
        text: "Transferring the caseload does not remove their access. Once their clients and supervisees are reassigned, see **Remove a team member's access**.",
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['No **Transfer Caseload** button', 'You are not signed in as an Org Admin.'],
          ['No clients appear after choosing the clinicians', 'That clinician has no clients. Check you picked the right person.'],
          ['The person you want is missing from **To clinician**', 'They are already the **From** clinician, or not on the team — see **Add a team member**.'],
          ['The wizard asked about Red Dots unexpectedly', 'Expected — that step only appears when the selected clients have open Red Dots.'],
          ['You transferred the wrong client', 'Run the transfer again in reverse. The Red Dot choice is **not** reversed automatically — check where those went.'],
          ['Red Dots are now unassigned', 'You chose the third option. Org Admins were notified and can re-route them.'],
          ['The new BCBA cannot see the client', 'Ask them to sign out and back in. Caseload is read at sign-in.'],
        ],
      },
    ],
  },
  {
    id: 'add-and-edit-goals',
    collectionId: 'clients',
    title: 'Add and edit goals',
    summary: "Build a client's goal tree by hand, or import a whole caseload's goals from a spreadsheet.",
    audience: 'BCBAs and Org Admins',
    duration: '~4 minutes',
    version: '1.0',
    updatedAt: '2026-08-26',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          'The **client** must already exist — see **Create a client record**.',
          'Goals are what Red Dots are raised against. A client with no goals cannot be the subject of a Red Dot, so this step gates the clinical workflow.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'How goals are organised',
        text: 'Goals sit in a three-level tree: **Domain → Program → Goal**, with optional **Targets** beneath a goal. A domain is the broad area (Communication, Daily Living), a program is the teaching procedure inside it (Requesting, Hygiene), and the goal is the measurable objective.',
      },
      {
        kind: 'heading',
        text: 'Choose the client',
      },
      {
        kind: 'step',
        num: '1',
        action: 'Open [[Goals]] from the sidebar and click a client in the left-hand list.',
        result: 'their goal tree on the right — or **No goals yet** if they have none, with **Add Domain** and **Import Goals** offered.',
      },
      {
        kind: 'image',
        src: '/assets/help/add-and-edit-goals/01.png',
        alt: 'The Goals page with a list of clients down the left side and, for the newly selected client, an empty state reading no goals yet with Add Domain and Import Goals buttons offered twice.',
        caption: '**A client with no goals.** Both routes are offered — build by hand, or import. Everything below works the same whether the client is empty or already has goals.',
      },
      {
        kind: 'heading',
        text: 'Route A — add a domain by hand',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Click [[Add Domain]], give it a [[Domain Name]], set the [[Status]], and click [[Create]].',
        result: 'the domain appear as a root of the tree, ready for a program beneath it.',
      },
      {
        kind: 'image',
        src: '/assets/help/add-and-edit-goals/02.png',
        alt: 'The Add Domain dialog headed adding a new root domain, with fields for domain name, a status dropdown offering Active, Met, On Hold and Discontinued, an optional short-term note, and date initiated and date met fields.',
        caption: '**Add Domain.** Only the name and status are needed; the dates and note are optional.',
      },
      {
        kind: 'text',
        text: 'The domain box also takes an optional short-term note, a [[Date Initiated]] and a [[Date Met]]. Status is one of:',
      },
      {
        kind: 'table',
        headers: ['Status', 'Means'],
        rows: [
          ['**Active**', 'Currently being taught. The normal starting state.'],
          ['**Met**', 'Mastered. Set **Date Met** when you choose this.'],
          ['**On Hold**', 'Paused, but expected to resume.'],
          ['**Discontinued**', 'Stopped and not coming back.'],
        ],
      },
      {
        kind: 'callout',
        label: 'Build downwards',
        text: 'Create the **domain** first, then add the **program** inside it, then the **goal**. Each level is added from the level above it — there is no way to create a goal without a parent.',
      },
      {
        kind: 'heading',
        text: 'Route B — import from a spreadsheet',
      },
      {
        kind: 'text',
        text: 'For a new client with a full programme, or a whole caseload transferring in, importing is far faster than building the tree by hand.',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Click [[Import Goals]], then [[Download Template]] and fill it in.',
        result: 'a CSV with these columns: [[goal_key]], [[parent_goal_key]], [[domain]], [[program]], [[goal]], [[target]], [[status]], [[date_initiated]], [[date_met]].',
      },
      {
        kind: 'text',
        text: 'One row per goal. Name the domain and program on the same row — the app builds the tree for you, reusing a domain when several rows name it. Use [[goal_key]] and [[parent_goal_key]] only when nesting a target beneath a goal.',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Drop the file on the upload area, click [[Upload & Validate]], check the preview, then click [[Import Goals]].',
        result: 'a confirmation — **Goals imported!** — with counts, and the tree populated behind it.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Validate is a dry run — read it',
        text: '**Upload & Validate** changes nothing. It shows a per-row preview classifying each as **New**, **Update**, **Reintroduce**, **Unchanged** or **Unmatched**, and reconciles them against the row total. Check that line before importing: **Unmatched** rows are ones it could not place, and they will not be created.',
      },
      {
        kind: 'image',
        src: '/assets/help/add-and-edit-goals/03.png',
        alt: 'The import preview showing counts of new, update, reintroduce and unmatched rows, a table listing each row with its domain, program, goal, status and outcome, and Back and Import Goals buttons.',
        caption: '**The preview.** Nothing is written until you click **Import Goals** here.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Three buttons say "Import Goals"',
        text: 'The one on the page opens the importer; the one on the preview commits it. If your click seems to do nothing, you have probably hit the page button and re-opened the upload step — check which screen you are on before clicking again.',
      },
      {
        kind: 'heading',
        text: 'Confirm you are finished',
      },
      {
        kind: 'image',
        src: '/assets/help/add-and-edit-goals/04.png',
        alt: "A client's populated goal tree listing goals with their status badge, a Red-Dot action beside each, and the date each was initiated.",
        caption: '**Goals in place.** Each goal shows its status, its initiated date, and a **Red-Dot** action — the entry point for flagging it later.',
        annotations: [
          { shape: 'badge', tone: 'ok', label: '✓', left: '6%', top: '9%' },
        ],
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['Rows counted as **Unmatched**', 'Those rows could not be placed and were skipped. Check the domain and program spelling, then re-import just those rows.'],
          ['Rows counted as **Unchanged**', 'Those goals already exist identically. Harmless — importing twice does not duplicate them.'],
          ['Duplicate domains in the tree', 'The domain name differs between rows — a stray space or different capitalisation. Names must match exactly to merge.'],
          ['An RBT cannot pick the goal on a Red Dot', "Check the goal is **Active**, and that the client is on that RBT's caseload."],
          ['You imported to the wrong client', 'The importer is scoped to whoever is selected on the left. Confirm the name in the panel header before uploading.'],
        ],
      },
    ],
  },
  {
    id: 'raise-a-red-dot',
    collectionId: 'clinical-workflows',
    title: 'Raise a Red Dot',
    summary: 'Flag a goal that is not progressing, so the overseeing BCBA can act on it.',
    audience: 'RBTs and Behavior Technicians',
    duration: '~5 minutes',
    version: '1.1',
    updatedAt: '2026-10-24',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          'The client must be **on your caseload** — you can only raise a Red Dot for your own clients. If a client is missing, ask an Org Admin to assign them to you.',
          "The **goal** you are flagging must already exist on that client's record.",
          'Have your recent session data to hand — you will be asked about progress, variability and consistency across technicians.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Which of the two options do I need?',
        text: 'The wizard offers a choice, and it changes the questions you get: Each option has a short video beside it if you are unsure.',
      },
      {
        kind: 'heading',
        text: 'Start the assessment',
      },
      {
        kind: 'step',
        num: '1',
        action: 'Open [[Red Dot Assessment]] from the sidebar, then click [[Start New Red Dot]].',
        result: 'the three-stage wizard — **Setup**, **Diagnostic**, **Review** — open on Setup.',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Choose the [[client]], then choose [[1 Red Dot]] or [[2 Call to Action]], then choose the [[goal]]. Click [[Continue]].',
        result: 'the **Diagnostic** stage, headed **Step 1 of 6** with the goal name beside it.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'The goal list only appears after you pick a client',
        text: 'There is no goal dropdown when the page first opens — it appears once a client is chosen. If you click **Continue** too early you will get **"Please fill in all fields"** with no obvious reason why: the goal is the field you have not filled.',
      },
      {
        kind: 'image',
        src: '/assets/help/raise-a-red-dot/01.png',
        alt: 'The Setup stage of the Red Dot wizard with a client selected, the Red Dot option chosen, and a goal selected from the dropdown that appeared after choosing the client.',
        caption: '**Setup, completed.** Client, option and goal all chosen — only then does **Continue** work.',
      },
      {
        kind: 'heading',
        text: 'Answer the diagnostic questions',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Answer each question [[Yes]] or [[No]], then click [[Next]].',
        result: 'the step counter advance. Read the **CLINICAL GUIDANCE** panel above each question — it explains what the question is really asking.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'The questions adapt to your answers',
        text: 'This is a branching assessment, not a fixed form. Answering **No** to "Is this client supported by more than one tech?" skips the follow-up about consistency across technicians, because it cannot apply. Your total step count changes as you go — do not be surprised when **Step 1 of 6** becomes **Step 2 of 7**.',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Where a **Yes** opens a text box, describe what you have observed in plain language.',
        result: 'the button change from [[Next]] to [[Review]] on the final question.',
      },
      {
        kind: 'image',
        src: '/assets/help/raise-a-red-dot/02.png',
        alt: 'The final diagnostic question asking whether the client displays challenging behaviors, answered Yes, which has revealed a text box asking the technician to list the challenging behaviors.',
        caption: '**A follow-up field.** Answering **Yes** to the challenging-behavior question opens a box that must be filled before you can continue. Be specific — the BCBA acts on this.',
      },
      {
        kind: 'heading',
        text: 'Review and submit',
      },
      {
        kind: 'step',
        num: '5',
        action: 'Check every answer on the [[Review & Submit]] page, then click [[Submit Red Dot]].',
        result: "the submission's own page, showing **Submitted**, the BCBA it went to, and a **Resolution Due** date.",
      },
      {
        kind: 'text',
        text: 'The review page restates the client, goal, option and the receiving BCBA before your answers. This is your last chance to correct anything — use [[Back]] if an answer is wrong.',
      },
      {
        kind: 'image',
        src: '/assets/help/raise-a-red-dot/03.png',
        alt: 'The submitted Red Dot page showing status Submitted, who submitted it, the goal, the resolution due date, the assigned BCBA, the type, the center location, a link to view the submission PDF, and an empty activity feed.',
        caption: "**Submitted.** The Red Dot is routed automatically to the client's overseeing BCBA — you do not choose a recipient.",
        annotations: [
          { shape: 'badge', tone: 'ok', label: '✓', left: '6%', top: '9%' },
        ],
      },
      {
        kind: 'callout',
        label: 'What happens next',
        items: [
          "It goes to the client's **overseeing BCBA** automatically — there is no recipient to pick.",
          "A **Resolution Due** date is set as soon as the Red Dot reaches a BCBA — 14 days later by default, or whatever **Resolution Timeline** your Org Admin has set. Past that date it counts as overdue on the BCBA's reporting.",
          'If the client has no overseeing BCBA, the Red Dot waits unassigned with **No deadline yet** until an Org Admin routes it — the clock starts then, not at submission.',
          'Your answers are saved as a **submission PDF** the BCBA can open.',
          'Anything the BCBA does appears under **Activity** on this page.',
        ],
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['Your client is not in the list', 'They are not on your caseload. Ask an Org Admin to assign them — see **Assign a client to a team member**.'],
          ['No goal dropdown', 'Choose the client first; the goal list appears afterwards.'],
          ['**"Please fill in all fields"**', 'Usually the goal. Check all three: client, option and goal.'],
          ['The goal you need is missing', 'It has not been added to the client yet. Ask your BCBA — see **Add and edit goals**.'],
          ['**Next** does nothing', 'A **Yes** has opened a text box further down. Fill it in.'],
          ['No assignee after submitting', 'The client has no overseeing BCBA set. Tell an Org Admin — see **Set the overseeing BCBA**.'],
          ['You submitted the wrong thing', 'You cannot delete a submission. Tell your BCBA, who can close it out.'],
        ],
      },
    ],
  },
  {
    id: 'work-and-close-a-red-dot',
    collectionId: 'clinical-workflows',
    title: 'Work and close a Red Dot',
    summary: 'Review a Red Dot raised by your technician, work the protocol tasks, and close it out.',
    audience: 'BCBAs and Clinical Directors',
    duration: '~4 minutes',
    version: '1.1',
    updatedAt: '2026-10-24',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          "You must be the client's **overseeing BCBA** — Red Dots route to you automatically.",
          'A technician must have submitted one. See **Raise a Red Dot** for their side.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Opening it already changes the status',
        text: 'A Red Dot arrives as **Submitted**. The moment you open it, it moves to **Under Review** — you do not click anything to accept it. That status is what the technician and your Clinical Director see, so opening one signals you have picked it up.',
      },
      {
        kind: 'heading',
        text: 'Review what was submitted',
      },
      {
        kind: 'step',
        num: '1',
        action: 'Open the Red Dot from your dashboard or from [[Red Dot Assessment]].',
        result: 'the submission header — **Status**, **Submitted By**, **Goal**, **Resolution Due**, **Assignees**, **Type** and **Location** — above **Summary** and **Protocols & Tasks** tabs.',
      },
      {
        kind: 'step',
        num: '2',
        action: "Click [[View submission PDF]] to read the technician's full answers.",
        result: 'their complete diagnostic responses, including anything they typed about challenging behavior.',
      },
      {
        kind: 'image',
        src: '/assets/help/work-and-close-a-red-dot/01.png',
        alt: 'A Red Dot open in the BCBA view showing status Under Review, who submitted it, the goal, resolution due date, assignee, type and center, with Put on Hold and Complete buttons and tabs for Summary and Protocols and Tasks.',
        caption: '**The BCBA view.****Put on Hold** and **Complete** sit at the top; **Protocols & Tasks** holds the clinical work.',
      },
      {
        kind: 'callout',
        label: 'Watch the Resolution Due date',
        text: 'The clock starts the moment the Red Dot is assigned to you, and the header shows the window it was given — for example **(14-day resolution timeline)**. Passing it on to another BCBA does not restart the clock; they inherit the time that is left. Past the date, the item counts as **Overdue** on the Red Dot report — which your Clinical Director can see broken down by clinician. If you cannot resolve it in time, put it on hold with a reason rather than letting it lapse silently.',
      },
      {
        kind: 'heading',
        text: 'Work the protocol tasks',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Open [[Protocols & Tasks]] and work through the tasks the system has attached.',
        result: 'structured clinical steps such as **Data Investigation**, **Data Collection Timing Decision Tree** and the **Functional Behavior Assessment Checklist (FBA)**.',
      },
      {
        kind: 'text',
        text: 'Use the comment box at the bottom and [[Send]] to record what you did or to ask the technician a question. Everything appears in the [[Activity]] feed, so the history stays with the Red Dot.',
      },
      {
        kind: 'heading',
        text: 'Close it out',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Click [[Complete]] when the concern is resolved, or [[Put on Hold]] if it cannot proceed yet.',
        result: 'a box asking for a justification. Both actions require one.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Closing with tasks still open is allowed — and logged',
        text: 'If protocol tasks are unfinished, the box lists exactly which ones and asks you to **provide justification to complete anyway**. Minimum five characters, and the note is **recorded against the Red Dot permanently**. Write a real clinical reason — "done" will sit in the record forever and tells a reviewer nothing.',
      },
      {
        kind: 'image',
        src: '/assets/help/work-and-close-a-red-dot/02.png',
        alt: 'The complete with open tasks dialog listing three still-open protocol tasks and a justification box stating a minimum of five characters and that the justification is logged.',
        caption: '**Completing with open tasks.** The dialog names every unfinished task before it lets you proceed.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'On Hold is the honest option',
        text: 'When a client is away, data collection has paused, or you are waiting on a caregiver, **Put on Hold** with a reason is better than completing prematurely. It keeps the item out of your active count without pretending the concern was resolved.',
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['No **Complete** or **Put on Hold** button', 'You are not the assignee. Only the overseeing BCBA can act on it.'],
          ['It moved to **Under Review** before you were ready', 'Expected — opening it does that. No harm; work it when you can.'],
          ['The justification box rejects your text', 'It needs at least five characters.'],
          ['You completed it by mistake', 'Say so in the **Activity** feed, and ask the technician to raise a fresh Red Dot if the concern stands.'],
          ['It is overdue and you cannot resolve it', 'Put it on hold with a reason. That is visible and defensible; letting it lapse is not.'],
        ],
      },
    ],
  },
  {
    id: 'add-a-team-member',
    collectionId: 'account',
    title: 'Add a team member',
    summary: "Create a colleague's account, give them a role and center, and decide when they can sign in.",
    audience: 'Org Admins',
    duration: '~3 minutes',
    version: '2.0',
    updatedAt: '2026-08-26',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          'You must be signed in as an **Org Admin**.',
          "Have the person's **work email address** — it becomes their sign-in name and cannot be changed later.",
          'Their **center** should already exist — see **Create and manage centers**.',
        ],
      },
      {
        kind: 'heading',
        text: 'Create the account',
      },
      {
        kind: 'step',
        num: '1',
        action: 'Open [[Team]] from the sidebar and click [[Add User]].',
        result: 'the **Add Team Member** box, with **Email**, **First Name** and **Last Name** marked required.',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Enter their [[Email]], [[First Name]] and [[Last Name]].',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Choose their [[Role]] and [[Location]].',
        result: 'both marked optional — but see the warning below.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Role and Location are labelled optional. Set them anyway.',
        text: 'A member with no role has no permissions and cannot do their job. A member with no location is invisible to center-scoped views and reads as **No location** in every people picker. The form will let you skip both — do not.',
      },
      {
        kind: 'text',
        text: 'The roles available are:',
      },
      {
        kind: 'table',
        headers: ['Role', 'For'],
        rows: [
          ['**RBT**', 'Behavior technicians. Raise Red Dots on their own caseload.'],
          ['**BCBA**', 'Supervise technicians; receive and close Red Dots for their clients.'],
          ['**Clinical Director**', 'Oversee BCBAs; organization-wide clinical visibility.'],
          ['**Org Admin**', 'Full administrative access — team, clients, centers, roles.'],
          ['**Individual**', 'Limited standalone access.'],
        ],
      },
      {
        kind: 'image',
        src: '/assets/help/add-a-team-member/01.png',
        alt: 'The Add Team Member box completed with an email address, first and last name, a role and a location chosen, and a grant login access immediately checkbox left unticked below.',
        caption: '**Add Team Member, completed.** The **Grant login access immediately** checkbox is the decision that matters most — see below.',
      },
      {
        kind: 'heading',
        text: 'Decide when they can sign in',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Tick or leave [[Grant login access immediately]], then click [[Add Team Member]].',
        result: 'the person in the team list with two separate indicators — a **status** and an **activation state**.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Two different things, easily confused',
        text: 'A new member created without login access reads **Active · Not Activated**: they exist, they can be assigned clients and appear in pickers, but they cannot sign in. That is the right state for someone who starts next month.',
      },
      {
        kind: 'image',
        src: '/assets/help/add-a-team-member/02.png',
        alt: 'The team list showing the newly added member with their email, role, an Active status and a Not Activated login state, alongside the existing activated members.',
        caption: '**Created.** The new member is **Active** but **Not Activated** — a real member who cannot yet log in.',
        annotations: [
          { shape: 'badge', tone: 'ok', label: '✓', left: '6%', top: '9%' },
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Ticking the box sends a real email',
        text: 'Granting login access immediately creates the sign-in and emails the person a temporary password, which they must change on first use. Do not tick it for someone who has not started — they will receive credentials before their first day. You can grant access later from their record.',
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['No **Add User** button', 'You are not signed in as an Org Admin.'],
          ['The email is already in use', 'The person already exists. Search the team list rather than creating a duplicate.'],
          ['They say they cannot sign in', 'Check the list — if it reads **Not Activated**, their login was never granted.'],
          ['They read **No location** in pickers', 'Location was left blank. Edit their record and set it.'],
          ['They can sign in but see nothing', 'No role. Edit their record and set one.'],
          ['Wrong email entered', 'It cannot be edited. Deactivate the record and create the person again correctly.'],
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: "What's next",
        text: 'A new clinician has no clients yet. To give them a caseload, see **Assign a client to a team member**. Adding a whole team at once is faster with [[Bulk Upload (CSV)]].',
      },
    ],
  },
  {
    id: 'remove-team-member-access',
    collectionId: 'account',
    title: "Remove a team member's access",
    summary: "Cut off someone's access when they leave or change role — and put it back if you need to.",
    audience: 'Org Admins',
    duration: '~3 minutes',
    version: '1.0',
    updatedAt: '2026-08-26',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          'You must be signed in as an **Org Admin**.',
          'Know whether the person is **leaving** or just **changing role** — the answer changes what you should do.',
          'If they hold clients, plan where those clients go **first**. See below.',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'There is one lever, not two',
        text: "You might expect to revoke someone's sign-in while keeping them on the team. **You cannot.** Once login access has been granted, the **Login Access** section becomes read-only — it reports **Login activated** and offers no control to undo it. The only way to stop someone signing in is **Deactivate**, which deactivates the whole member. Plan around that.",
      },
      {
        kind: 'heading',
        text: 'Understand the two states first',
      },
      {
        kind: 'table',
        headers: ['Indicator', 'Means', 'Can you change it?'],
        rows: [
          ['**Active** / **Inactive**', 'Whether the person is a current member of the organization.', 'Yes — **Deactivate** and **Reactivate**'],
          ['**Activated** / **Not Activated**', 'Whether their login has ever been switched on.', 'Only one way — you can grant it, not revoke it'],
        ],
      },
      {
        kind: 'text',
        text: 'So a departing member ends up **Inactive · Activated**. That combination looks contradictory but is correct: their login was switched on once, and deactivating the member is what actually blocks them.',
      },
      {
        kind: 'heading',
        text: 'Remove the access',
      },
      {
        kind: 'step',
        num: '1',
        action: "Open [[Team]] and click the person's name.",
        result: 'their record, with **Login Access** and **Status** sections and a **Deactivate** button.',
      },
      {
        kind: 'image',
        src: '/assets/help/remove-team-member-access/01.png',
        alt: "A team member's record showing their role, location and supervisor, a Login Access section reading login activated with no button beside it, and a Status section reading active since a date with a Deactivate button.",
        caption: '**An active member.** Note that **Login Access** has no control — only **Status** does.',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Click [[Deactivate]].',
        result: 'the change apply **straight away**. Status flips to **Inactive** and the button becomes **Reactivate**.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'There is no confirmation step',
        text: '**Deactivate** takes effect the instant you click it — no "are you sure", no undo toast. Read the name at the top of the record before you click. If you get the wrong person, click **Reactivate** immediately.',
      },
      {
        kind: 'image',
        src: '/assets/help/remove-team-member-access/02.png',
        alt: 'The same member record after deactivation, now showing an Inactive badge beside their name, a Status section reading Inactive, and a Reactivate button in place of Deactivate.',
        caption: '**Deactivated.****Login Access** still reads **Login activated** — that is expected and does not mean they can still sign in.',
      },
      {
        kind: 'heading',
        text: 'Confirm they are locked out',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Check the team list — their row should read **Inactive**.',
        result: 'any sign-in attempt they make now fails.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'What the departing person actually sees — warn your service desk',
        text: 'They do **not** get told their access was removed. They get: "Something went wrong and we can\'t sign you in right now. Please try again later." That reads like a temporary outage, so people retry and then raise a ticket. If you are deactivating someone who does not know it is coming, tell them — otherwise your support queue finds out first.',
      },
      {
        kind: 'image',
        src: '/assets/help/remove-team-member-access/03.png',
        alt: "The sign-in page showing an error reading something went wrong and we can't sign you in right now, please try again later, after a deactivated member attempted to log in.",
        caption: '**What they see.** Misleading, but access is genuinely revoked.',
      },
      {
        kind: 'heading',
        text: 'Putting access back',
      },
      {
        kind: 'text',
        text: 'Open the record and click [[Reactivate]]. Status returns to **Active** and their **existing password still works** — they do not go through first-time sign-in again.',
      },
      {
        kind: 'image',
        src: '/assets/help/remove-team-member-access/04.png',
        alt: 'The member record after reactivation, showing an Active status with the original member-since date and the Deactivate button restored.',
        caption: '**Reactivated.** Nothing was lost — role, location and login all survive.',
        annotations: [
          { shape: 'badge', tone: 'ok', label: '✓', left: '6%', top: '9%' },
        ],
      },
      {
        kind: 'heading',
        text: 'Do these first, in this order',
      },
      {
        kind: 'table',
        headers: ['Order', 'Do', 'Why'],
        rows: [
          ['1', 'Move their **clients** to another clinician', 'Deactivating does not reassign anyone. Clients are left with a clinician who cannot sign in.'],
          ['2', 'Reassign their **overseeing-BCBA** clients', 'New Red Dots would route to someone who has left — see **Set the overseeing BCBA**.'],
          ['3', 'Close or reassign their **open Red Dots**', 'Open items keep their original assignee and quietly go overdue.'],
          ['4', 'Reassign anyone they **supervise**', 'Supervisees are left reporting to an inactive person.'],
          ['5', '**Then** deactivate', 'Nothing is orphaned.'],
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Changing role rather than leaving?',
        text: 'Do not deactivate. Use [[Edit]] on their record to change the role or location — their history, caseload and login all stay intact.',
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['No **Deactivate** button', 'You are not signed in as an Org Admin.'],
          ['You want to block sign-in but keep them on the team', 'Not supported today. Deactivating the member is the only lever.'],
          ['**Login Access** still says **Login activated**', 'Expected on an inactive member. **Status** is what governs access.'],
          ['You deactivated the wrong person', 'Click **Reactivate** straight away. Nothing is lost.'],
          ['They report a vague sign-in error', 'That is the deactivation. The message is misleading, not a fault.'],
          ['Clients now have no clinician', 'Reactivate, reassign the caseload, then deactivate again.'],
          ['You need the person fully erased', 'Not possible, by design. Clinical records carry retention obligations — deactivation is the intended end state.'],
        ],
      },
    ],
  },
  {
    id: 'read-the-red-dot-report',
    collectionId: 'insights',
    title: 'Read the Red Dot report',
    summary: 'Find the Red Dot analytics report, filter it, read the headline numbers, and open a single submission.',
    audience: 'BCBAs, Clinical Directors, Org Admins',
    duration: '~4 minutes',
    version: '2.1',
    updatedAt: '2026-10-24',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          'You must hold **Manage Red Dot** permission. **BCBAs**, **Clinical Directors**, **Org Admins** and **Executives** have it by default.',
          '**RBTs cannot open this report.** They can submit Red Dots, but not view the analytics.',
          'There must be at least one submitted Red Dot or Call to Action for the report to show numbers.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Your role decides how much you see',
        items: [
          '**Org Admin, Clinical Director, Executive** — every client in the organization.',
          '**BCBA** — your own caseload only.',
        ],
      },
      {
        kind: 'heading',
        text: 'Open the report',
      },
      {
        kind: 'step',
        num: '1',
        action: 'In the left sidebar, open [[INSIGHTS]] and click [[Red Dot]].',
        result: 'the **Red Dot Analytics** page, with a filter bar, seven headline numbers, and a set of panels below them.',
      },
      {
        kind: 'callout',
        label: "Don't confuse the two Red Dot menu items",
        text: '[[WORKFLOWS › Red Dot Assessment]] is where you **fill in and submit** an assessment. [[INSIGHTS › Red Dot]] — this report — is where you **read the results** across clients.',
      },
      {
        kind: 'image',
        src: '/assets/help/read-the-red-dot-report/01.png',
        alt: 'The Red Dot Analytics page showing the Insights Red Dot item selected in the left sidebar, a filter bar with Type, Status, Clinician, Client, Location, State and Submitted controls, seven headline metric tiles, and the Submissions over time and Submissions Per Client panels beginning below.',
        caption: '**Red Dot Analytics.** The sidebar item (1), the filter bar (2), the seven headline numbers (3) and **Export** (4).',
        annotations: [
          { shape: 'box', left: '0.8%', top: '41.1%', width: '16.1%', height: '4.9%' },
          { shape: 'badge', label: '1', left: '0.8%', top: '43.5%' },
          { shape: 'box', left: '20%', top: '18%', width: '77.8%', height: '17.7%' },
          { shape: 'badge', label: '2', left: '18.1%', top: '21.5%' },
          { shape: 'box', tone: 'ok', left: '19.5%', top: '38.4%', width: '78.5%', height: '12%' },
          { shape: 'badge', tone: 'ok', label: '3', left: '18.1%', top: '42.5%' },
          { shape: 'box', tone: 'warn', left: '92.2%', top: '10.7%', width: '5.6%', height: '4%' },
          { shape: 'badge', tone: 'warn', label: '4', left: '88.6%', top: '11.1%' },
        ],
      },
      {
        kind: 'heading',
        text: 'Read the seven headline numbers',
      },
      {
        kind: 'text',
        text: 'These tiles always describe **the rows left after your filters** — change a filter and every tile recalculates.',
      },
      {
        kind: 'table',
        headers: ['Tile', 'What it counts'],
        rows: [
          ['**Total Workflow Runs**', 'Every submission matching your filters — Red Dots and Calls to Action together.'],
          ['**Red Dots**', 'Submissions where the clinician chose **Option 1** (a Red Dot).'],
          ['**CTA Runs**', 'Submissions where the clinician chose **Option 2** (a Call to Action).'],
          ['**Active**', 'Anything still open — Submitted, Under Review, In Progress or On Hold.'],
          ['**Completed**', 'Anything finished — Completed or Closed.'],
          ['**Overdue**', '**Active** items that are past their **Resolution Due** date.'],
          ['**Avg Resolution (days)**', 'Average days from submission to completion, across completed items only. Shown as a whole number.'],
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Overdue is not a separate pile',
        items: [
          'Red Dots + CTA Runs = Total Workflow Runs',
          'Active + Completed = Total Workflow Runs',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Avg Resolution can look misleadingly good',
        text: 'It is calculated only from items that have actually been completed, and it is rounded to whole days — anything opened and closed the same day counts as **0**. If most of your work is still Active, the average is describing a small, finished minority. Read it next to **Completed**, never on its own.',
      },
      {
        kind: 'callout',
        label: 'A zero is not always a problem',
        text: '**Overdue 0** means nothing has passed its due date yet — in a young organization that is simply true, not a fault. Likewise **Avg Resolution 0** where the only completed items were closed the same day they were raised. Check **Completed** and the due dates in **Workflow Detail** before reading anything into either number.',
      },
      {
        kind: 'heading',
        text: 'Narrow the report with filters',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Set any combination of the filters in the bar at the top, then read the numbers again.',
        result: 'every tile, panel and table on the page update together — they always describe the same filtered set.',
      },
      {
        kind: 'table',
        headers: ['Filter', 'Use it to'],
        rows: [
          ['[[TYPE]]', 'Show only Red Dots, or only CTAs.'],
          ['[[STATUS]]', 'Show only Submitted, Under Review or Completed items.'],
          ['[[CLINICIAN]]', "Type a name to follow one person's workload."],
          ['[[CLIENT]]', "Type a name to see one client's full history."],
          ['[[LOCATION]]', 'Limit to one center.'],
          ['[[STATE]]', 'Limit to one state.'],
          ['[[SUBMITTED]]', 'Limit to a date range.'],
        ],
      },
      {
        kind: 'text',
        text: 'Click [[Reset Filters]] to clear all of them and return to your full view.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Filter by clicking, not just typing',
        text: 'Rows in **Resolution Time by Clinician** and **Submissions by Location** are clickable — clicking one applies that clinician or center as a filter for the whole page. It is the quickest way to go from "this center looks bad" to "show me only that center".',
      },
      {
        kind: 'heading',
        text: 'What each panel tells you',
      },
      {
        kind: 'table',
        headers: ['Panel', 'Read it for'],
        rows: [
          ['**Submissions over time**', 'Whether submission volume is rising or falling. With only a few days of history it shows a single point — that is expected, not a rendering fault.'],
          ['**Submissions Per Client**', 'Which clients generate the most Red Dots — the top rows are where clinical attention is concentrated.'],
          ['**Resolution Time by Clinician**', "Each BCBA's total, completed, active and average days. Rows are grouped by the **BCBA the item is assigned to**, not the technician who raised it. A dash in **Avg Days** means they have not completed anything yet."],
          ['**Resolution Time Trend**', 'Whether resolution is getting faster or slower month over month.'],
          ['**Workflow Detail**', 'The underlying rows, one per submission. Sortable and clickable.'],
          ['**Red Dot Response Breakdown**', 'How the assessment questions were answered across submissions, as percentages.'],
          ['**Submissions by Location**', 'Total, active, completed and overdue per center — the fastest way to spot a struggling site.'],
        ],
      },
      {
        kind: 'image',
        src: '/assets/help/read-the-red-dot-report/02.png',
        alt: 'The Resolution Time by Clinician table listing clinicians with total, completed, active and average day counts, beside the Resolution Time Trend chart with a Supervisor View button above it.',
        caption: '**Resolution Time by Clinician** and the trend chart. Click any clinician row to filter the whole page to that person.',
      },
      {
        kind: 'callout',
        label: 'Supervisor View',
        text: 'The button above the trend chart switches it between one **organization-wide average** line and **one line per clinician**. Click [[Supervisor View]] to compare clinicians against each other; click [[Show Average]] to go back to the single line.',
      },
      {
        kind: 'heading',
        text: 'Open a single submission',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Scroll to [[Workflow Detail]] and click any row.',
        result: "that submission's own page, showing the full assessment and its history.",
      },
      {
        kind: 'text',
        text: 'Use the arrows in the [[Workflow Detail]] column headers to sort — by **Submitted** to see the newest first, by **Resolution Due** to see what falls due next, or by **Overdue** to bring the late work to the top. The heading shows how many records are currently in view, for example **Workflow Detail (36 records)**.',
      },
      {
        kind: 'text',
        text: '**Resolution Due** is the date each item has to be resolved by. It is blank for an item nobody has been assigned yet — the clock only starts on assignment. It is included in the export.',
      },
      {
        kind: 'image',
        src: '/assets/help/read-the-red-dot-report/03.png',
        alt: 'The Workflow Detail table listing individual submissions with client, goal, type, submitted date, status, overdue, resolution, clinician and location columns.',
        caption: '**Workflow Detail.** One row per submission. Click a row to open it; click a column heading to sort.',
      },
      {
        kind: 'heading',
        text: 'Export what you are looking at',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Click [[Export]] at the top right, then [[Export to Excel]].',
        result: "a spreadsheet download containing the report's tables.",
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Exports contain client names',
        text: 'The export is clinical data. Save it somewhere approved, share it only with people entitled to see those clients, and delete it when you are done. Do not email it to yourself.',
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['No **Red Dot** item under **INSIGHTS**', 'Your role does not include **Manage Red Dot**. RBTs never see it. Ask an Org Admin.'],
          ["Your totals are lower than a colleague's", 'Expected if you are a BCBA — you see your caseload, they may see the whole organization.'],
          ['All panels are empty', 'A filter is excluding everything. Click **Reset Filters**.'],
          ['A clinician shows — in **Avg Days**', 'They have no completed items yet, so there is nothing to average. Not an error.'],
          ['**Avg Resolution** looks too good', 'Check **Completed**. A low average over very few completed items is not meaningful.'],
          ['A row shows location **Unknown**', 'That client has no center set. Fix it on the client record — see **Create a client record**.'],
          ['Fidelity, Task Completion or Clinician Performance show "Coming soon"', 'Only the Red Dot report is available today. The others are not built yet.'],
        ],
      },
    ],
  },
  {
    id: 'create-and-manage-centers',
    collectionId: 'organization-setup',
    title: 'Create and manage centers',
    summary: 'Add a new center, then find it, edit it, or take it out of service.',
    audience: 'Org Admins',
    duration: '~3 minutes',
    version: '1.0',
    updatedAt: '2026-08-26',
    blocks: [
      {
        kind: 'callout',
        label: 'Before you start',
        items: [
          'You must be signed in as an **Org Admin**.',
          "Have the center's **address and phone number** to hand — the city and state are used throughout the app to scope clinicians and clients.",
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'A center is where people and clients live',
        text: 'Centers are not just an address book. Team members and clients are each assigned to a center, and several screens filter by it. Create the center **before** you try to assign anyone to it.',
      },
      {
        kind: 'heading',
        text: 'Create a center',
      },
      {
        kind: 'step',
        num: '1',
        action: 'Open [[Centers]] from the sidebar, then click [[Create Center]].',
        result: 'the **Create Center** box, with the center name field ready for typing.',
      },
      {
        kind: 'step',
        num: '2',
        action: 'Fill in [[Center Name]] and [[Street Address]], then choose the [[State]].',
        result: 'the **City** field become editable once a state is chosen.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'City stays greyed out until you pick a state',
        text: 'This catches people out. **City** is disabled when the box first opens — choose the **State** first and it becomes editable. **Country** stays fixed at United States and cannot be changed.',
      },
      {
        kind: 'step',
        num: '3',
        action: 'Enter the [[City]], [[ZIP Code]] and [[Phone Number]], then click [[Create Center]].',
        result: 'the box close and your new center at the top of the list, marked **Active** with **0** clinicians and **0** clients.',
      },
      {
        kind: 'image',
        src: '/assets/help/create-and-manage-centers/01.png',
        alt: 'The Create Center box completed for Millrace Center, showing the center name, street address, city, state, ZIP code, fixed country and phone number, with Cancel and Create Center buttons at the bottom.',
        caption: '**Create Center, completed.** Only **City** is marked required, but fill in the full address — it appears on the center record and in transfer history.',
      },
      {
        kind: 'image',
        src: '/assets/help/create-and-manage-centers/02.png',
        alt: 'The Centers list showing three centers — Millrace, Ardsley and Halstead — each with city and state, an Active status badge, clinician and client counts, and edit and deactivate icons at the end of each row.',
        caption: '**Centers.****Create Center** (1), the new center (3), and the per-row **edit** and **deactivate** icons (4). A brand-new center starts at 0 and 0.',
        annotations: [
          { shape: 'box', left: '86.8%', top: '10.9%', width: '11%', height: '4.4%' },
          { shape: 'badge', label: '1', left: '90.5%', top: '16.5%' },
          { shape: 'box', tone: 'ok', left: '20%', top: '22.7%', width: '77.8%', height: '6.9%' },
          { shape: 'badge', tone: 'ok', label: '3', left: '17.9%', top: '24.4%' },
          { shape: 'box', tone: 'warn', left: '91.4%', top: '23.9%', width: '5.7%', height: '4.6%' },
          { shape: 'badge', tone: 'warn', label: '4', left: '88.6%', top: '24.4%' },
        ],
      },
      {
        kind: 'heading',
        text: 'Find, edit or retire a center',
      },
      {
        kind: 'step',
        num: '4',
        action: 'Use the icons at the end of a row — the **pencil** to edit, the **power symbol** to deactivate. To see everything about a center instead, click its **name**.',
        result: "either the **Edit Center** box, or the center's own page.",
      },
      {
        kind: 'text',
        text: 'The center page opens on [[Overview]] — the address, phone and when it was created — with four more tabs beside it:',
      },
      {
        kind: 'table',
        headers: ['Tab', 'Shows'],
        rows: [
          ['[[Overview]]', 'Address, phone, and who created the center and when.'],
          ['[[Roster]]', 'The clinicians and clients currently attached to this center.'],
          ['[[Change History]]', 'Edits made to the center record over time.'],
          ['[[Transfer History]]', 'People and clients moved into or out of this center.'],
          ['[[Center Scope]]', 'How this center limits what its staff can see.'],
        ],
      },
      {
        kind: 'image',
        src: '/assets/help/create-and-manage-centers/03.png',
        alt: 'The Millrace Center page showing an Active badge, city, clinician and client counts, tabs for Overview, Roster, Change History, Transfer History and Center Scope, the address and phone under Center Information with an Edit button, and administrative created and last-updated dates.',
        caption: '**The center page.****Edit** beside **Center Information** opens the same form as the pencil icon in the list.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        label: 'Deactivating is not deleting',
        text: 'The power symbol takes a center **out of service** — it stops appearing as a choice for new assignments, but the record and its history stay. Check the **Clinicians** and **Clients** counts first: deactivating a center that still has people attached leaves them without a working center. Move them somewhere else before you retire it.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        label: 'Adding many centers at once',
        text: '[[Bulk Upload]] beside **Create Center** takes a file instead of the one-at-a-time form. Use it for an initial setup; use **Create Center** for a single addition.',
      },
      {
        kind: 'heading',
        text: 'If something looks wrong',
      },
      {
        kind: 'table',
        headers: ['What you see', 'What to do'],
        rows: [
          ['**City** is greyed out', 'Choose the **State** first — City stays disabled until then.'],
          ['No **Create Center** button', 'You are not signed in as an Org Admin.'],
          ['The new center is missing from the list', 'The **All Statuses** filter may be set to Inactive. Reset it, or search by name.'],
          ['Clinician and client counts stay at 0', 'Expected for a new center. Counts rise as you assign people to it.'],
          ['You retired the wrong center', 'Set the status filter to **Inactive**, find it, and reactivate — nothing was deleted.'],
        ],
      },
    ],
  },
];
